import { Button, TableItem } from 'components/atoms';
import PopupDeleteWarning from 'components/molecules/popup/delete-warning/PopupDeleteWarning';
import { Table } from 'components/organisms';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftBold, PiNavigationArrow, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useClearContractFuture } from 'services/product-contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { useGetTrackContractFuture } from 'services/tracks/settlementInfo';
import { ITrackMutationForm } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const TrackContractsFuture = () => {
  const trackId = useCustomParamId('id');
  usePermissionGuard('ContractPermissionsLIST**');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const navigateToNewWindow = useNewWindowNavigate();

  const { status, values }: IFormikContextType<ITrackMutationForm> =
    useFormikContext<ITrackMutationForm>();

  const future = useGetTrackContractFuture(trackId);

  const clearContractFuture = useClearContractFuture();

  const onClearContractFuture = async (actions: PopupActions) =>
    await clearContractFuture.mutateAsync(
      {
        productId: trackId,
        productType: PRODUCT_TYPE.Track,
      },
      {
        onSuccess: () => {
          future.refetch();
          actions.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(
                  status?.displayMode
                    ? `/track/${trackId}/contracts`
                    : `/track/edit/${trackId}/contracts`
                )
              }>
                            <Button.Description description={t('button.back')} />

                            <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
          topbarContent={
            <PopupDeleteWarning
              onRemove={onClearContractFuture}
              disabledTrigger={!future.data?.contracts.length}
              name={`${t('contract-template.contract.what-future')} ${
                values.mainTitle.title
              }`}
              trigger={
                <Button
                  isClear
                  variant={BUTTON_VARIANT.remove}
                  onlyIcon
                  disabled={!future.data?.contracts.length}>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              }
            />
          }
        />
      }>
      <Table
        size='compact'
        loading={future.isLoading}
        data={{
          nodes:
            future.data?.contracts.map(c => ({
              ...c,
              id: c.contractId,
            })) ?? [],
        }}
        columns={[
          {
            label: `${t('input.label.global')}`,
            renderCell: ({ labelName: name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.contract`),
            renderCell: contract => (
              <TableItem>
                <TableItem.Text text={contract.contractName} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.date`),
            renderCell: contract => (
              <TableItem>
                <TableItem.Text text={`${formatDate(contract.startDate)}`} />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t(`input.label.division`),
            renderCell: ({ divisionPercentage, id: index }) => (
              <TableItem>
                <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ id: index, labelId, contractId }) => (
              <TableItem justifyPosition='end'>
                <Button
                  isClear
                  onlyIcon
                  onClick={() =>
                    navigateToNewWindow(
                      `label/${labelId}/contract/${contractId}/form`
                    )
                  }>
                                    <Button.Description description={t('button.go-to-form')} />

                                    <Button.Icon
                    icon={<PiNavigationArrow transform='scale(-1,1)' />}
                  />
                </Button>
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default TrackContractsFuture;
